import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { VideosLayout } from "@/components/videos/VideosLayout";
import { getHistory, clearHistory, HistoryItem } from "@/lib/videosApi";
import { Trash2, Play } from "lucide-react";

const formatRelativeTime = (ts: number): string => {
  const diff = Date.now() - ts;
  const minutes = Math.floor(diff / (1000 * 60));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} d ago`;
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

export const VideosHistoryPage: React.FC = () => {
  const [history, setHistory] = useState<HistoryItem[]>([]);

  useEffect(() => {
    const update = () => setHistory(getHistory());
    update();
    window.addEventListener("jnn-videos-history-updated", update);
    return () =>
      window.removeEventListener("jnn-videos-history-updated", update);
  }, []);

  const handleClear = () => {
    clearHistory();
    setHistory([]);
  };

  return (
    <VideosLayout>
      <SEOHead
        title="Continue Watching | JNN Homes Northern Virginia"
        description="Pick up where you left off — your recently watched JNN Homes vacation rental management videos."
        noIndex={true}
      />

      <div className="space-y-8">
        <nav className="flex items-center gap-2 text-xs text-slate-500 font-medium">
          <Link to="/videos" className="hover:text-[#9A5207]">
            Videos
          </Link>
          <span>›</span>
          <span className="text-slate-900 font-semibold">History</span>
        </nav>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">
              Continue watching
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Pick up right where you left off.
            </p>
          </div>
          {history.length > 0 && (
            <button
              onClick={handleClear}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:border-red-300 hover:text-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-[#D97706]"
            >
              <Trash2 className="w-4 h-4" /> Clear history
            </button>
          )}
        </div>

        {history.length === 0 ? (
          <div className="p-12 rounded-2xl bg-slate-50 border border-slate-200 text-center space-y-4">
            <div className="inline-flex p-3 rounded-full bg-slate-100 text-slate-400">
              <Play className="w-6 h-6" />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-bold text-slate-900">
                No videos watched yet
              </h2>
              <p className="text-sm text-slate-600 max-w-sm mx-auto">
                Videos you watch will appear here so you can pick up where you
                left off.
              </p>
            </div>
            <Link
              to="/videos"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#B35A06] text-white font-bold text-xs hover:bg-[#9A5207] transition-colors shadow-sm"
            >
              Browse all videos
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 min-[1400px]:grid-cols-5 gap-4">
            {history.map((item) => (
              <Link
                key={`${item.id}-${item.ts}`}
                to={item.href}
                className="group flex flex-col space-y-2.5"
              >
                <div className="relative aspect-[9/16] w-full rounded-[14px] overflow-hidden bg-slate-100 border border-slate-200/80 group-hover:border-[#E8850F] transition-colors">
                  <img
                    src={`https://i.ytimg.com/vi/${item.id}/hqdefault.jpg`}
                    alt={`Thumbnail for ${item.t}`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-slate-900/20 group-hover:bg-slate-900/30 transition-colors flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-[#E8850F] text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="w-4 h-4 fill-white ml-0.5" />
                    </div>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="line-clamp-2 text-xs sm:text-sm font-semibold text-slate-900 group-hover:text-[#9A5207] leading-snug">
                    {item.t}
                  </p>
                  <p className="text-[11px] text-slate-500 font-medium">
                    Played {formatRelativeTime(item.ts)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </VideosLayout>
  );
};
